import {
  SkillsImportApplyErrorCode,
  type SkillsImportApplyError,
  type SkillsImportResult,
} from '@core/tellent-skills-types';

export const SKILLS_IMPORT_INVALID_CODE = 'skills_import_invalid';

// We normally avoid building translation keys by concatenation. This file is a
// deliberate exception: a single error code can resolve to one of many keys
// under this shared namespace, and spelling out every full key inline would be
// far noisier than scoping them to this constant.
const PREFIX = 'tlc.positions.import_skills_flow.result_dialog.failed.issues';

type TranslateFn = (key: string, params?: Record<string, string | number>) => string;

interface PathSegment {
  key: string;
  index: number | null;
}

interface PathContext {
  skillName: string | null;
  proficiencyName: string | null;
  positionName: string | null;
  groupName: string | null;
  field: string | null;
}

const SUPPORTED_FIELDS = new Set(['name', 'description', 'level']);

function parsePath(path: string): PathSegment[] {
  const segments: PathSegment[] = [];
  // Matches each dotted path segment with an optional array index,
  // e.g. "skills[0].proficiencies[1].description" →
  // key "skills" idx 0, key "proficiencies" idx 1, key "description" idx null.
  // Group 1 is the segment name; the non-capturing `(?:\[(\d+)\])?` captures the optional [index].
  const regex = /([a-zA-Z_]+)(?:\[(\d+)\])?/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(path)) !== null) {
    segments.push({ key: match[1], index: match[2] !== undefined ? Number(match[2]) : null });
  }
  return segments;
}

function resolveContext(path: string, result: SkillsImportResult): PathContext {
  const context: PathContext = {
    skillName: null,
    proficiencyName: null,
    positionName: null,
    groupName: null,
    field: null,
  };

  let skill: SkillsImportResult['skills'][number] | undefined;
  let proficiency: SkillsImportResult['skills'][number]['proficiencies'][number] | undefined;

  for (const { key, index } of parsePath(path)) {
    switch (key) {
      case 'skills':
        if (index !== null) {
          skill = result.skills[index];
          context.skillName = skill?.name ?? null;
        }
        break;
      case 'proficiencies':
        if (skill && index !== null) {
          proficiency = skill.proficiencies[index];
          context.proficiencyName = proficiency?.name ?? null;
        }
        break;
      case 'positions':
        if (index !== null) {
          context.positionName = proficiency
            ? (proficiency.positions[index]?.positionName ?? null)
            : (result.positions[index]?.name ?? null);
        }
        break;
      case 'groups':
        if (index !== null) {
          context.groupName = result.groups[index]?.name ?? null;
        }
        break;
      case 'group':
        context.groupName = skill?.group?.name ?? null;
        break;
      default:
        if (SUPPORTED_FIELDS.has(key)) {
          context.field = key;
        }
    }
  }

  return context;
}

function entityPhrase(t: TranslateFn, context: PathContext): string | null {
  if (context.proficiencyName) {
    return t(`${PREFIX}.entity.proficiency`, { name: context.proficiencyName });
  }
  if (context.skillName) {
    return t(`${PREFIX}.entity.skill`, { name: context.skillName });
  }
  if (context.positionName) {
    return t(`${PREFIX}.entity.position`, { name: context.positionName });
  }
  if (context.groupName) {
    return t(`${PREFIX}.entity.group`, { name: context.groupName });
  }
  return null;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function changesetSubject(t: TranslateFn, context: PathContext): string | null {
  const field = context.field ? t(`${PREFIX}.field.${context.field}`) : null;
  const entity = entityPhrase(t, context);

  if (field && entity) {
    return t(`${PREFIX}.subject_with_entity`, { field, entity });
  }
  if (field) {
    return field;
  }
  return entity ? capitalize(entity) : null;
}

function pickName(
  contextName: string | null,
  details: Record<string, unknown>,
  ...detailKeys: string[]
): string | null {
  if (contextName) {
    return contextName;
  }
  for (const key of detailKeys) {
    const value = details[key];
    if (value !== null && value !== undefined && value !== '') {
      return String(value);
    }
  }
  return null;
}

function buildChangesetIssue(
  t: TranslateFn,
  context: PathContext,
  details: Record<string, unknown>,
  generic: string,
): string {
  const subject = changesetSubject(t, context);
  if (!subject) {
    return generic;
  }
  const opts = <Record<string, unknown>>(details['opts'] ?? {});
  if (details['validation'] === 'length' && opts['kind'] === 'max') {
    return t(`${PREFIX}.changeset.too_long`, { subject, max: Number(opts['count']) });
  }
  if (details['validation'] === 'length' && opts['kind'] === 'min') {
    return t(`${PREFIX}.changeset.too_few`, { subject, min: Number(opts['count']) });
  }
  if (details['validation'] === 'required') {
    return t(`${PREFIX}.changeset.required`, { subject });
  }
  return t(`${PREFIX}.changeset.default`, { subject });
}

function buildIssue(
  t: TranslateFn,
  error: SkillsImportApplyError,
  result: SkillsImportResult,
): string {
  const context = resolveContext(error.path, result);
  const details = error.details ?? {};
  const generic = t(`${PREFIX}.generic`);

  switch (error.errorCode) {
    case SkillsImportApplyErrorCode.ChangesetValidationFailed:
      return buildChangesetIssue(t, context, details, generic);

    case SkillsImportApplyErrorCode.SkillGroupRefNotFound: {
      const name = pickName(context.skillName, details, 'groupName');
      return name
        ? t(`${PREFIX}.skill_group_not_found`, { name })
        : t(`${PREFIX}.skill_group_not_found_unknown`);
    }

    case SkillsImportApplyErrorCode.ProficiencyPositionRefNotFound: {
      const name = pickName(context.proficiencyName, details);
      return name
        ? t(`${PREFIX}.proficiency_position_not_found`, { name })
        : t(`${PREFIX}.proficiency_position_not_found_unknown`);
    }

    case SkillsImportApplyErrorCode.ResourceNotFound: {
      const name = pickName(context.positionName, details, 'resourceId');
      return name ? t(`${PREFIX}.position_not_found`, { name }) : generic;
    }

    case SkillsImportApplyErrorCode.PositionNameAlreadyExists: {
      const name = pickName(context.positionName, details, 'name');
      return name ? t(`${PREFIX}.position_name_exists`, { name }) : generic;
    }

    case SkillsImportApplyErrorCode.DuplicateGroupId:
    case SkillsImportApplyErrorCode.DuplicateNewGroupName: {
      const name = pickName(context.groupName, details, 'name', 'id');
      return name ? t(`${PREFIX}.duplicate_group`, { name }) : generic;
    }

    case SkillsImportApplyErrorCode.DuplicateMatchedSkillId:
    case SkillsImportApplyErrorCode.DuplicateNewSkillName: {
      const name = pickName(context.skillName, details, 'name', 'matchedSkillId');
      return name ? t(`${PREFIX}.duplicate_skill`, { name }) : generic;
    }

    case SkillsImportApplyErrorCode.DuplicateExistingPositionGuid:
    case SkillsImportApplyErrorCode.DuplicateNewPositionName: {
      const name = pickName(context.positionName, details, 'name');
      return name ? t(`${PREFIX}.duplicate_position`, { name }) : generic;
    }

    case SkillsImportApplyErrorCode.DuplicateProficiencyLevel: {
      const skillName = pickName(context.skillName, {});
      const level = pickName(null, details, 'level');
      return skillName && level
        ? t(`${PREFIX}.duplicate_proficiency_level`, { name: skillName, level })
        : generic;
    }

    case SkillsImportApplyErrorCode.DuplicateMatchedProficiencyId: {
      const skillName = pickName(context.skillName, {});
      return skillName ? t(`${PREFIX}.duplicate_proficiency`, { name: skillName }) : generic;
    }

    default:
      return generic;
  }
}

export function mapApplyErrorsToIssues(
  errors: SkillsImportApplyError[],
  result: SkillsImportResult,
  t: TranslateFn,
): string[] {
  const seen = new Set<string>();
  for (const error of errors) {
    seen.add(buildIssue(t, error, result));
  }
  return [...seen];
}
